import { useEffect, useState } from "react";
import { useInView } from "@/hooks/useInView";

interface ProductVideoProps {
  src: string;
  poster: string;
  alt: string;
  className?: string;
}

/**
 * Vídeo vertical de produto com autoplay, mute, loop e playsInline.
 * - Pausa quando sai da viewport (IntersectionObserver).
 * - Enquanto o vídeo real não carrega, exibe o poster com efeito Ken Burns
 *   para manter a sensação de movimento.
 * - Vídeos reais: basta colocar os arquivos em /public/videos/.
 */
export default function ProductVideo({
  src,
  poster,
  alt,
  className = "",
}: ProductVideoProps) {
  const { ref, inView } = useInView<HTMLVideoElement>(0.3);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (inView) {
      video.play().catch(() => {
        /* vídeo ainda não disponível — mantém poster */
      });
    } else {
      video.pause();
    }
  }, [inView, ref]);

  return (
    <div className={`relative h-full w-full overflow-hidden bg-foreground-950 ${className}`}>
      {/* Poster com efeito Ken Burns (fallback de movimento) */}
      <img
        src={poster}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover object-top animate-ken-burns"
      />

      {/* Vídeo real por cima quando disponível */}
      <video
        ref={ref}
        src={src}
        poster={poster}
        muted
        loop
        playsInline
        preload="metadata"
        aria-label={alt}
        onPlaying={() => setPlaying(true)}
        onCanPlay={() => setPlaying(true)}
        className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-500 ${
          playing ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
}